import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ASSETS } from "@/lib/brand";

export default function Admin() {
  const [tab, setTab] = useState("news");
  const [listings, setListings] = useState([]);
  const [news, setNews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newsForm, setNewsForm] = useState({ title: "", excerpt: "", body: "", category: "Market", source: "", link: "" });
  const [listForm, setListForm] = useState({ title: "", location: "", status: "Sold", price: "", image_url: "", year: "" });
  const [uploading, setUploading] = useState(false);
  const nav = useNavigate();

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const uploadImage = async (file) => {
    if (!file) return null;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${BACKEND}${data.url}`;
      toast.success("Image uploaded");
      return fullUrl;
    } catch (err) {
      toast.error("Upload failed");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const loadAll = async () => {
    try {
      await api.get("/admin/me");
    } catch {
      nav("/admin/login");
      return;
    }
    const [l, n, m] = await Promise.all([
      api.get("/listings"),
      api.get("/news"),
      api.get("/admin/messages"),
    ]);
    setListings(l.data);
    setNews(n.data);
    setMessages(m.data);
  };

  useEffect(() => { loadAll(); /* eslint-disable-line */ }, []);

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch {}
    localStorage.removeItem("wg_admin_token");
    nav("/admin/login");
  };

  const submitNews = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/news", newsForm);
      toast.success("News post published");
      setNewsForm({ title: "", excerpt: "", body: "", category: "Market", source: "", link: "" });
      loadAll();
    } catch { toast.error("Failed"); }
  };
  const submitListing = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/listings", listForm);
      toast.success("Listing added");
      setListForm({ title: "", location: "", status: "Sold", price: "", image_url: "", year: "" });
      loadAll();
    } catch { toast.error("Failed"); }
  };
  const delNews = async (id) => { await api.delete(`/admin/news/${id}`); loadAll(); };
  const delListing = async (id) => { await api.delete(`/admin/listings/${id}`); loadAll(); };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="border-b border-white/10 px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src={ASSETS.logo} alt="WG" className="h-8" />
          <span className="overline text-white/50">Admin Portal</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="overline text-white/60 hover:text-[#CBBFAE]">View Site</Link>
          <button data-testid="admin-logout-button" onClick={logout} className="overline text-white/60 hover:text-[#CBBFAE]">Logout</button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-8 p-8">
        <aside className="col-span-12 md:col-span-2 space-y-2">
          {[
            { k: "news", label: "News", testid: "admin-sidebar-news" },
            { k: "listings", label: "Listings", testid: "admin-sidebar-listings" },
            { k: "messages", label: "Messages", testid: "admin-sidebar-messages" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={t.testid}
              onClick={() => setTab(t.k)}
              className={`block w-full text-left px-4 py-3 overline ${tab === t.k ? "bg-[#121212] text-[#CBBFAE] border-l-2 border-[#CBBFAE]" : "text-white/60 hover:text-white"}`}
            >
              {t.label}
            </button>
          ))}
        </aside>

        <main className="col-span-12 md:col-span-10 space-y-10">
          {tab === "news" && (
            <>
              <div className="border border-white/10 p-8">
                <div className="overline text-[#CBBFAE] mb-6">New Journal Post</div>
                <form onSubmit={submitNews} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input required data-testid="admin-news-title" className="line-input" placeholder="Title" value={newsForm.title} onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })} />
                  <input data-testid="admin-news-category" className="line-input" placeholder="Category (e.g. Buyer / Seller / Market)" value={newsForm.category} onChange={(e) => setNewsForm({ ...newsForm, category: e.target.value })} />
                  <input data-testid="admin-news-source" className="line-input" placeholder="Source (e.g. The Mortgage Reports)" value={newsForm.source} onChange={(e) => setNewsForm({ ...newsForm, source: e.target.value })} />
                  <input required data-testid="admin-news-excerpt" className="line-input md:col-span-2" placeholder="Excerpt / quote (short summary)" value={newsForm.excerpt} onChange={(e) => setNewsForm({ ...newsForm, excerpt: e.target.value })} />
                  <textarea required data-testid="admin-news-body" className="line-input md:col-span-2 min-h-[140px] pt-4" placeholder="Full body" value={newsForm.body} onChange={(e) => setNewsForm({ ...newsForm, body: e.target.value })} />
                  <input data-testid="admin-news-link" className="line-input md:col-span-2" placeholder="Read More link (https://...)" value={newsForm.link} onChange={(e) => setNewsForm({ ...newsForm, link: e.target.value })} />
                  <button data-testid="admin-news-submit" className="md:col-span-2 border border-[#CBBFAE]/50 hover:border-[#CBBFAE] rounded-full py-3 overline text-white mt-4">Publish</button>
                </form>
              </div>
              <div className="space-y-3">
                {news.map((n) => (
                  <div key={n.id} data-testid="admin-news-row" className="flex items-center justify-between border border-white/10 px-6 py-4">
                    <div>
                      <div className="font-serif text-xl">{n.title}</div>
                      <div className="overline text-white/40 mt-1">{n.category}</div>
                    </div>
                    <button data-testid="admin-news-delete" onClick={() => delNews(n.id)} className="overline text-white/60 hover:text-red-400">Delete</button>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "listings" && (
            <>
              <div className="border border-white/10 p-8">
                <div className="overline text-[#CBBFAE] mb-6">New Listing</div>
                <form onSubmit={submitListing} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input required data-testid="admin-listing-title" className="line-input" placeholder="Title" value={listForm.title} onChange={(e) => setListForm({ ...listForm, title: e.target.value })} />
                  <input required data-testid="admin-listing-location" className="line-input" placeholder="Location" value={listForm.location} onChange={(e) => setListForm({ ...listForm, location: e.target.value })} />
                  <input data-testid="admin-listing-status" className="line-input" placeholder="Status (Sold / Active / Coming Soon)" value={listForm.status} onChange={(e) => setListForm({ ...listForm, status: e.target.value })} />
                  <input data-testid="admin-listing-price" className="line-input" placeholder="Price ($)" value={listForm.price} onChange={(e) => setListForm({ ...listForm, price: e.target.value })} />
                  <input data-testid="admin-listing-year" className="line-input" placeholder="Year" value={listForm.year} onChange={(e) => setListForm({ ...listForm, year: e.target.value })} />
                  <input required data-testid="admin-listing-image" className="line-input md:col-span-2" placeholder="Image URL (paste or upload below)" value={listForm.image_url} onChange={(e) => setListForm({ ...listForm, image_url: e.target.value })} />
                  <label className="md:col-span-2 flex items-center gap-4 border border-dashed border-white/20 hover:border-[#CBBFAE] rounded-md px-5 py-4 cursor-pointer transition-colors">
                    <span className="overline text-white/70">{uploading ? "Uploading…" : listForm.image_url ? "Replace Image" : "Upload Image"}</span>
                    <input
                      data-testid="admin-listing-file-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const url = await uploadImage(e.target.files?.[0]);
                        if (url) setListForm({ ...listForm, image_url: url });
                        e.target.value = "";
                      }}
                    />
                    {listForm.image_url && <img src={listForm.image_url} alt="preview" className="ml-auto h-12 w-16 object-cover rounded" />}
                  </label>
                  <button data-testid="admin-listing-submit" className="md:col-span-2 border border-[#CBBFAE]/50 hover:border-[#CBBFAE] rounded-full py-3 overline text-white mt-4">Add Listing</button>
                </form>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {listings.map((l) => (
                  <div key={l.id} data-testid="admin-listing-row" className="border border-white/10 overflow-hidden">
                    <img src={l.image_url} alt={l.title} className="w-full h-40 object-cover" />
                    <div className="p-4 flex items-center justify-between">
                      <div>
                        <div className="font-serif text-lg">{l.title}</div>
                        <div className="overline text-white/40 mt-1">{l.status} · {l.year}</div>
                      </div>
                      <button data-testid="admin-listing-delete" onClick={() => delListing(l.id)} className="overline text-white/60 hover:text-red-400">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "messages" && (
            <div className="space-y-3">
              <div className="overline text-[#CBBFAE] mb-4">Client Inquiries</div>
              {messages.length === 0 && <div className="text-white/40">No messages yet.</div>}
              {messages.map((m) => (
                <div key={m.id} data-testid="admin-message-row" className="border border-white/10 p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-serif text-xl">{m.name}</div>
                    <div className="overline text-white/40">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  <div className="overline text-white/50 mb-3">{m.email} · {m.phone || "—"} · {m.agent || "Any agent"}</div>
                  <p className="text-white/75 leading-relaxed">{m.message}</p>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
